import Button from "@/components/Button";
import { arrow } from "@/designSystemIcons";
import { fireGTMEvent } from "@/functions/fireGTMEvent";
import { lock as lockIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import {
  canUseClubImportSelector,
  getUserSelector,
} from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

interface SectionListItemProps {
  title: React.ReactNode;
  children: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

export const SectionListItem: React.FC<SectionListItemProps> = ({
  icon,
  title,
  children,
  disabled,
  onClick,
}) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  return (
    <div
      className={cn(
        "flex flex-row justify-between items-center h-[52px] px-3",
        disabled
          ? "text-dark-disabled pointer-events-none cursor-default"
          : "text-dark-tertiary group-hover:text-white",
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        {icon && (
          <div
            className={cn(
              "h-7 w-7 flex items-center justify-center",
              isMobile && "w-8 h-8",
            )}
          >
            {icon}
          </div>
        )}
        <div className={cn("text-sm leading-none", isMobile && "text-base")}>
          {title}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
};
SectionListItem.displayName = "SectionListItem";

interface SectionListSectionProps {
  text?: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  searchbar?: React.ReactNode;
  searchResults?: React.ReactNode;
}

export const SectionListSection: React.FC<SectionListSectionProps> = ({
  text,
  icon,
  children,
  searchbar,
  searchResults,
}) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <span
          className={cn("text-dark-tertiary text-sm", isMobile && "text-base")}
        >
          {text}
        </span>
        {icon}
      </div>
      {searchbar && <div className="">{searchbar}</div>}
      {searchResults && (
        <div className="mb-3 rounded-lg bg-[#333A42]">{searchResults}</div>
      )}
      <div className="rounded-lg bg-[#333A42]">{children}</div>
    </div>
  );
};

interface SectionListProps {
  header?: React.ReactNode;
  accordion?: boolean;
  locked?: boolean;
  children: React.ReactNode;
  closedClassName?: string;
  isOpen?: boolean;
}

interface SectionListComponent extends React.FC<SectionListProps> {
  Item: React.FC<SectionListItemProps>;
  Section: React.FC<SectionListSectionProps>;
}

const SectionList: SectionListComponent = ({
  header,
  accordion = false,
  locked,
  children,
  closedClassName,
  isOpen: isOpenProp,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenProp ?? true);
  const canUseClubImport = useSelector(canUseClubImportSelector);
  const navigate = useNavigate();
  const isLocked = useMemo(
    () => locked && !canUseClubImport,
    [locked, canUseClubImport],
  );

  const toggleAccordion = () => {
    if (accordion) setIsOpen((prev) => !prev);
  };

  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  return (
    <div>
      <header
        className={cn(
          "bg-panel-foreground flex items-center px-4 h-11 text-dark-secondary",
          isMobile && "h-12.5",
          accordion || isLocked ? "justify-between" : "justify-start",
          accordion && !isLocked && "cursor-pointer",
          !isOpen && closedClassName,
        )}
        onClick={() => {
          if (isLocked) {
            return;
          }
          toggleAccordion();
        }}
      >
        <span
          className={cn(
            "text-sm",
            isMobile && "text-base",
            locked && "text-dark-tertiary",
          )}
        >
          {header}
        </span>
        {isLocked ? (
          <div className="flex items-center gap-2">
            <NavLink
              className="text-xs text-dark-tertiary underline mt-auto mb-1"
              to="/get-sbc-solver"
              state={{ from: window.location.pathname }}
            >
              Learn More
            </NavLink>
            <Button
              variant="secondary"
              label="Unlock"
              icon={<div className="h-4 w-4">{lockIcon}</div>}
              onClick={() => navigate("/subscription")}
            />
          </div>
        ) : (
          accordion && (
            <div
              className={cn(
                "w-3 h-3 text-dark-tertiary",
                isOpen ? "" : "rotate-180",
              )}
            >
              {arrow}
            </div>
          )
        )}
      </header>
      {(!accordion || isOpen) && (
        <div className="relative overflow-hidden">
          <main className="h-auto">{children}</main>
        </div>
      )}
    </div>
  );
};

SectionList.Item = SectionListItem;
SectionList.Section = SectionListSection;

export default SectionList;
